import type { ComponentProps } from "react";
import { DEFAULT_CURRENCY } from "@/data/constants";
import { YEARLY_LEDGER_ENABLED } from "../flags.dashboard";
import type { EstimateIntervalData } from "@/components/shared/interval-switch/estimate-interval-change";
import { useLedgerSwitch } from "../hooks/useLedgerSwitch";
import { useYearlyUpsale } from "../hooks/useYearlyUpsale";
import { DashboardService } from "../service.dashboard";
import type { TLedgerAmounts } from "../types.dashboard";
import YearlyPlanSaleBanner from "./YearlyPlanSaleBanner";
import YearlyPlanSaleLedger from "./YearlyPlanSaleLedger";

const COPY = DashboardService.Content.yearlyPlanSaleLedger;

type YearlyUpsaleProps = ComponentProps<typeof YearlyPlanSaleBanner>;

function YearlyUpsaleSkeleton() {
  return (
    <div
      role="status"
      className="h-28 w-full animate-shimmer rounded-lg bg-surface-light bg-linear-to-r from-transparent from-35% via-white/90 via-50% to-transparent to-65% bg-size-[200%_100%] motion-reduce:animate-none sm:h-35"
    >
      <span className="sr-only">{COPY.loading}</span>
    </div>
  );
}

interface YearlyUpsaleLedgerProps extends YearlyUpsaleProps {
  amounts: TLedgerAmounts;
  estimate: EstimateIntervalData;
}

/** Owns the switch flow, so dialog and promo changes re-render only the ledger */
function YearlyUpsaleLedger({
  amounts,
  estimate,
  ownedPlanList,
  billingPeriod,
  isV1,
  loading,
}: YearlyUpsaleLedgerProps) {
  const ledger = useLedgerSwitch({
    amounts,
    estimate,
    planCurrency: ownedPlanList[0]?.pricing.currency ?? DEFAULT_CURRENCY,
    billingPeriod,
    planNames: ownedPlanList.map((plan) => plan.planName),
    isV1,
  });

  return <YearlyPlanSaleLedger {...ledger} disabled={loading} />;
}

function YearlyUpsaleSwitch(props: YearlyUpsaleProps) {
  const upsale = useYearlyUpsale({
    ownedPlanList: props.ownedPlanList,
    billingPeriod: props.billingPeriod,
    isV1: props.isV1,
  });

  if (upsale.kind === "loading") {
    return <YearlyUpsaleSkeleton />;
  }

  if (upsale.kind === "banner") {
    return <YearlyPlanSaleBanner {...props} />;
  }

  return (
    <YearlyUpsaleLedger
      {...props}
      amounts={upsale.amounts}
      estimate={upsale.estimate}
    />
  );
}

const YearlyUpsale = (props: YearlyUpsaleProps) => {
  if (!YEARLY_LEDGER_ENABLED) {
    return <YearlyPlanSaleBanner {...props} />;
  }

  return <YearlyUpsaleSwitch {...props} />;
};

export default YearlyUpsale;
